import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AnimatedLogo from './AnimatedLogo';
import { useTheme } from '../context/ThemeContext';
import Magnetic from './ux/Magnetic';

const { FiMenu, FiX, FiCommand, FiCalendar, FiCpu, FiSun, FiMoon } = FiIcons;

const mobileMenuVariants = {
  closed: { opacity: 0, y: '-100%', transition: { duration: 0.5, ease: [0.76, 0, 0.24, 1] } },
  open: { opacity: 1, y: '0%', transition: { duration: 0.5, ease: [0.76, 0, 0.24, 1] } }
};

const linkVariants = {
  closed: { opacity: 0, y: 20 },
  open: i => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.3 + (i * 0.1), duration: 0.5, ease: [0.76, 0, 0.24, 1] }
  })
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navLinks = [
    { title: 'Home', path: '/' },
    { title: 'Services', path: '/services' },
    { title: 'AI Integration', path: '/ai-integration' },
    { title: 'Capabilities', path: '/capabilities' },
    { title: 'Engagement', path: '/engagement' },
    { title: 'Insights', path: '/insights' },
    { title: 'Careers', path: '/careers' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${scrolled || mobileMenuOpen ? 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800 py-4 shadow-sm' : 'bg-transparent py-6'}`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <Magnetic>
            <Link to="/" className="relative flex items-center cursor-pointer z-50">
              <div className="absolute w-20 h-20 bg-cyan-100 dark:bg-cyan-900/30 blur-xl opacity-40 rounded-full pointer-events-none"></div>
              <AnimatedLogo className="h-16 w-16 relative z-10" />
            </Link>
          </Magnetic>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <div className="flex items-center gap-6 mr-2">
              <Link to="/" className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${location.pathname === '/' ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-cursor="hover">Home</Link>
              <Link to="/services" className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${location.pathname === '/services' ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-cursor="hover">Services</Link>
              <Link to="/ai-integration" className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors flex items-center gap-1.5 ${location.pathname === '/ai-integration' ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-cursor="hover">
                <SafeIcon icon={FiCpu} className="text-xs" />
                AI Integration
              </Link>
              <Link to="/capabilities" className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${location.pathname === '/capabilities' ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-cursor="hover">Capabilities</Link>
              <Link to="/engagement" className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${location.pathname === '/engagement' ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-cursor="hover">Engagement</Link>
              <Link to="/insights" className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${location.pathname === '/insights' ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-cursor="hover">Insights</Link>
              <Link to="/careers" className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${location.pathname === '/careers' ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`} data-cursor="hover">Careers</Link>
            </div>
            <div className="flex items-center gap-4">
              <Magnetic>
                <button 
                  onClick={toggleTheme} 
                  className="p-2 rounded-full text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700 bg-slate-50 hover:bg-white dark:bg-slate-800 dark:hover:bg-slate-700"
                  aria-label="Toggle Dark Mode"
                >
                  <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className="text-lg" />
                </button>
              </Magnetic>
              <Magnetic strength={0.2}>
                <Link
                  to="/book-call"
                  className="px-6 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] bg-slate-900 dark:bg-white text-white dark:text-slate-900 border border-slate-900 dark:border-white hover:bg-transparent dark:hover:bg-transparent hover:text-slate-900 dark:hover:text-white transition-all duration-300 flex items-center gap-2 shadow-lg shadow-slate-200 dark:shadow-none"
                >
                  <SafeIcon icon={FiCalendar} />
                  Book a Call
                </Link>
              </Magnetic>
            </div>
          </div>

          {/* Mobile Toggle & Theme */}
          <div className="lg:hidden flex items-center gap-4 z-50">
            <button onClick={toggleTheme} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white p-2">
               <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className="text-xl" />
            </button>
            <button 
              className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white p-2 flex flex-col justify-center items-center w-8 h-8 relative" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className={`w-6 h-[2px] bg-current transition-all duration-300 absolute ${mobileMenuOpen ? 'rotate-45' : '-translate-y-2'}`}></span>
              <span className={`w-6 h-[2px] bg-current transition-all duration-300 absolute ${mobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`w-6 h-[2px] bg-current transition-all duration-300 absolute ${mobileMenuOpen ? '-rotate-45' : 'translate-y-2'}`}></span>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Full Screen Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            variants={mobileMenuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="fixed inset-0 z-50 bg-white dark:bg-slate-950 flex flex-col pt-32 pb-12 px-6 overflow-y-auto" 
          >
            <div className="flex-1 flex flex-col justify-center gap-8">
              {navLinks.map((link, i) => (
                <motion.div key={i} custom={i} variants={linkVariants} initial="closed" animate="open" exit="closed" className="overflow-hidden">
                  <Link 
                    to={link.path} 
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-3xl sm:text-5xl font-black uppercase tracking-tighter ${location.pathname === link.path ? 'text-cyan-500' : 'text-slate-900 dark:text-white'} hover:text-cyan-500 transition-colors inline-block`}
                  >
                    {link.title}
                  </Link>
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              custom={navLinks.length} 
              variants={linkVariants} 
              initial="closed" animate="open" exit="closed"
              className="mt-12"
            >
              <Link 
                to="/book-call" 
                onClick={() => setMobileMenuOpen(false)}
                className="w-full py-5 text-center text-sm font-black uppercase tracking-[0.2em] bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center gap-3 active:scale-95 transition-transform"
              >
                <SafeIcon icon={FiCalendar} className="text-lg" />
                Book a Call
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;